import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import {
    Activity, TrendingUp, Clock, Zap, Radio, ChevronRight, Brain, Target,
    Database, BarChart2, Shield, Users, Bell, MessageSquare, Heart, Flame,
    ArrowUpRight, ArrowDownRight, Crosshair
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
    ReferenceLine, Line, LineChart, BarChart, Bar, Cell, ComposedChart
} from 'recharts';

/* ═══ PRE-MATCH COMMAND CENTER ═══ */
function PreMatchPanel({ match, countdown, navigate }) {
    const [prediction, setPrediction] = useState(null);
    const [intel, setIntel] = useState(null);
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [predRes, intelRes, schedRes] = await Promise.all([
                    API.get('/god-mode-verdict').catch(() => ({ data: null })),
                    API.get('/intelligence/pipeline/stats').catch(() => ({ data: null })),
                    API.get('/intelligence/auto-scraper/status').catch(() => ({ data: null })),
                ]);
                setPrediction(predRes.data);
                setIntel(intelRes.data);
                setSchedule(schedRes.data);
            } catch (e) { console.error(e); }
            setLoading(false);
        };
        load();
    }, []);

    const ha = match.home_team;
    const aa = match.away_team;
    const haColor = ha?.color || '#00E5FF';
    const aaColor = aa?.color || '#FF2D95';
    const predA = prediction?.final_verdict?.team_a_pct || 50;
    const predB = prediction?.final_verdict?.team_b_pct || 50;
    const winner = prediction?.final_verdict?.predicted_winner;
    const confidence = prediction?.final_verdict?.confidence;
    const masterVerdict = prediction?.master_gpt?.verdict;
    const engines = prediction?.all_engines || [];
    const intelData = prediction?.intelligence_data;
    const edgeFactors = prediction?.edge_factors || [];
    const riskFactors = prediction?.risk_factors || [];
    const topEngines = [...engines].sort((a, b) => b.weight - a.weight).slice(0, 8);
    const engineChartData = topEngines.map(e => ({
        name: e.engine.replace(/\s*\(.*\)/, '').slice(0, 16),
        pct: e.team_a_pct,
        weight: Math.round(e.weight * 100),
    }));

    return (
        <div className="space-y-4" data-testid="pre-match-panel">
            <div className="glass-card p-6 border-l-4 border-l-yellow-500" data-testid="pre-match-header">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-6">
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: haColor }}>{ha?.short_name}</div>
                            <div className="text-[10px] text-white/30">{ha?.name}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-xs text-white/20 tech-mono">VS</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: aaColor }}>{aa?.short_name}</div>
                            <div className="text-[10px] text-white/30">{aa?.name}</div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] text-white/30 tech-mono">KICKOFF IN</div>
                        <div className="heading-primary text-3xl text-yellow-400">{countdown}</div>
                        <div className="text-[10px] text-white/20 mt-1">{match.venue?.name}, {match.venue?.city}</div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="glass-card p-8 text-center">
                    <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                    <div className="text-xs text-white/30 mt-3">Loading 20-engine prediction...</div>
                </div>
            ) : (
                <>
                    {prediction?.status === 'ready' && (
                        <div className="glass-card p-6 border border-cyan-500/20" data-testid="pre-match-prediction">
                            <div className="flex items-center gap-2 mb-4">
                                <Brain className="w-4 h-4 text-cyan-400" />
                                <h3 className="text-sm text-white font-semibold">GOD MODE v2.0 — 20 ENGINE PREDICTION</h3>
                            </div>
                            <div className="grid grid-cols-12 gap-4 mb-4">
                                <div className="col-span-5 text-right">
                                    <div className="text-3xl font-bold" style={{ color: haColor }}>{predA}%</div>
                                    <div className="text-xs text-white/40">{ha?.short_name}</div>
                                </div>
                                <div className="col-span-2 flex items-center justify-center">
                                    <div className="text-xs text-white/20 tech-mono">VS</div>
                                </div>
                                <div className="col-span-5">
                                    <div className="text-3xl font-bold" style={{ color: aaColor }}>{predB}%</div>
                                    <div className="text-xs text-white/40">{aa?.short_name}</div>
                                </div>
                            </div>
                            <div className="h-3 rounded-full overflow-hidden flex mb-4">
                                <div className="h-full transition-all duration-500" style={{ width: `${predA}%`, backgroundColor: haColor }} />
                                <div className="h-full transition-all duration-500" style={{ width: `${predB}%`, backgroundColor: aaColor }} />
                            </div>
                            {masterVerdict && (
                                <div className="bg-white/5 rounded-lg p-3 mb-4 border-l-2 border-l-cyan-500">
                                    <div className="text-[10px] text-cyan-400 tech-mono mb-1">GPT-5.2 MASTER VERDICT</div>
                                    <div className="text-sm text-white/70">{masterVerdict}</div>
                                </div>
                            )}
                            {winner && (
                                <div className="flex items-center gap-4 bg-white/5 rounded-lg p-3 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <Target className="w-4 h-4 text-green-400" />
                                        <span className="text-xs text-white/40">Predicted Winner:</span>
                                        <span className="text-sm text-green-400 font-bold">{winner?.short_name}</span>
                                    </div>
                                    {confidence && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs text-white/40">Confidence:</span>
                                            <span className="text-sm text-amber-400 font-bold">{Math.round(confidence * 100)}%</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    {engineChartData.length > 0 && (
                        <div className="glass-card p-5" data-testid="pre-match-engines">
                            <div className="flex items-center gap-2 mb-3">
                                <BarChart2 className="w-4 h-4 text-amber-400" />
                                <h3 className="text-xs text-white/40 tech-mono">TOP ENGINE PREDICTIONS</h3>
                            </div>
                            <ResponsiveContainer width="100%" height={200}>
                                <BarChart data={engineChartData} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#555', fontSize: 9 }} />
                                    <YAxis dataKey="name" type="category" width={120} tick={{ fill: '#888', fontSize: 9 }} />
                                    <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8 }} />
                                    <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
                                        {engineChartData.map((e, i) => (
                                            <Cell key={i} fill={e.pct > 55 ? haColor : e.pct < 45 ? aaColor : '#64748b'} fillOpacity={0.7} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                    {intelData && (
                        <div className="glass-card p-5" data-testid="pre-match-intel">
                            <div className="flex items-center gap-2 mb-3">
                                <Database className="w-4 h-4 text-cyan-400" />
                                <h3 className="text-xs text-white/40 tech-mono">INTELLIGENCE DATA</h3>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { label: 'Social Sentiment', a: intelData.social_sentiment?.team_a_avg?.toFixed(3), b: intelData.social_sentiment?.team_b_avg?.toFixed(3) },
                                    { label: 'Google Trends', a: intelData.google_trends?.team_a_interest, b: intelData.google_trends?.team_b_interest },
                                    { label: 'Fantasy XI', a: intelData.fantasy_xi?.team_a_score?.toFixed(1), b: intelData.fantasy_xi?.team_b_score?.toFixed(1) },
                                    { label: 'Impact Player', a: intelData.impact_player?.team_a?.best, b: intelData.impact_player?.team_b?.best },
                                ].map((item, i) => (
                                    <div key={i} className="flex justify-between bg-white/5 rounded p-2">
                                        <span className="text-xs text-white/40">{item.label}</span>
                                        <span className="text-xs">
                                            <span style={{ color: haColor }}>{item.a || 'N/A'}</span>
                                            <span className="text-white/20 mx-1">vs</span>
                                            <span style={{ color: aaColor }}>{item.b || 'N/A'}</span>
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {(edgeFactors.length > 0 || riskFactors.length > 0) && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {edgeFactors.length > 0 && (
                                <div className="glass-card p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Shield className="w-4 h-4 text-green-400" />
                                        <h3 className="text-xs text-white/40 tech-mono">EDGE FACTORS</h3>
                                    </div>
                                    {edgeFactors.map((f, i) => (
                                        <div key={i} className="text-xs text-green-400/70 bg-green-500/5 rounded p-1.5 mb-1 border border-green-500/10">+ {f}</div>
                                    ))}
                                </div>
                            )}
                            {riskFactors.length > 0 && (
                                <div className="glass-card p-5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Zap className="w-4 h-4 text-red-400" />
                                        <h3 className="text-xs text-white/40 tech-mono">RISK FACTORS</h3>
                                    </div>
                                    {riskFactors.map((f, i) => (
                                        <div key={i} className="text-xs text-red-400/70 bg-red-500/5 rounded p-1.5 mb-1 border border-red-500/10">! {f}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    <div className="text-center text-[10px] text-white/10 tech-mono">
                        PRE-MATCH COMMAND CENTER — {engines.length} ENGINES | AUTO-SWITCHES TO LIVE ON MATCH START
                    </div>
                </>
            )}
        </div>
    );
}

/* ═══ SENTIMENT TRACKER (Enhanced with Event Mapping) ═══ */
function SentimentTracker({ matchData }) {
    const [sentiment, setSentiment] = useState(null);
    const [events, setEvents] = useState(null);
    const matchId = matchData?.match?.id;

    useEffect(() => {
        const load = () => {
            API.get('/live/sentiment').then(r => setSentiment(r.data)).catch(() => {});
            if (matchId) {
                API.get(`/live/sentiment-events/${matchId}`).then(r => setEvents(r.data)).catch(() => {});
            }
        };
        load();
        const iv = setInterval(load, 25000);
        return () => clearInterval(iv);
    }, [matchId]);

    if (!sentiment?.current?.total_reports && !events?.total_events) return null;

    const ha = matchData?.batting_team || matchData?.match?.home_team;
    const aa = matchData?.bowling_team || matchData?.match?.away_team;
    const haColor = ha?.color || '#00E5FF';
    const aaColor = aa?.color || '#FF822A';
    const sA = sentiment?.current?.team_a_sentiment || 5;
    const sB = sentiment?.current?.team_b_sentiment || 5;
    const src = sentiment?.sources_breakdown || {};
    const timeline = events?.timeline || [];
    const sentEventsList = events?.events || [];

    return (
        <div className="glass-card p-5" data-testid="sentiment-tracker">
            <div className="flex items-center gap-2 mb-4">
                <Heart className="w-4 h-4 text-pink-400" />
                <h3 className="heading-primary text-sm text-white">LIVE SENTIMENT TRACKER</h3>
                <span className="text-[9px] px-2 py-0.5 bg-pink-500/20 text-pink-400 rounded tech-mono">REAL-TIME</span>
                {sentEventsList.length > 0 && (
                    <span className="text-[9px] px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded tech-mono">{sentEventsList.length} EVENTS</span>
                )}
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 rounded-lg" style={{ background: `${haColor}10`, border: `1px solid ${haColor}30` }}>
                    <div className="text-xs text-white/40 mb-1">{sentiment?.match?.home_team?.short_name || ha?.short_name || 'TEAM A'}</div>
                    <div className="text-2xl font-bold" style={{ color: haColor }}>{sA?.toFixed(1) || '5.0'}</div>
                    <div className="text-[10px] text-white/30">/10 sentiment</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                        {sA > 5.5 ? <ArrowUpRight className="w-3 h-3 text-green-400" /> : <ArrowDownRight className="w-3 h-3 text-red-400" />}
                        <span className={`text-[10px] ${sA > 5.5 ? 'text-green-400' : 'text-red-400'}`}>{sA > 5.5 ? 'POSITIVE' : 'NEUTRAL'}</span>
                    </div>
                </div>
                <div className="text-center p-3 rounded-lg" style={{ background: `${aaColor}10`, border: `1px solid ${aaColor}30` }}>
                    <div className="text-xs text-white/40 mb-1">{sentiment?.match?.away_team?.short_name || aa?.short_name || 'TEAM B'}</div>
                    <div className="text-2xl font-bold" style={{ color: aaColor }}>{sB?.toFixed(1) || '5.0'}</div>
                    <div className="text-[10px] text-white/30">/10 sentiment</div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                        {sB > 5.5 ? <ArrowUpRight className="w-3 h-3 text-green-400" /> : <ArrowDownRight className="w-3 h-3 text-red-400" />}
                        <span className={`text-[10px] ${sB > 5.5 ? 'text-green-400' : 'text-red-400'}`}>{sB > 5.5 ? 'POSITIVE' : 'NEUTRAL'}</span>
                    </div>
                </div>
            </div>
            <div className="h-2 rounded-full overflow-hidden flex mb-4">
                <div className="h-full transition-all duration-700" style={{ width: `${(sA / (sA + sB)) * 100}%`, backgroundColor: haColor }} />
                <div className="h-full transition-all duration-700" style={{ width: `${(sB / (sA + sB)) * 100}%`, backgroundColor: aaColor }} />
            </div>

            {/* Event-Mapped Sentiment Timeline Chart */}
            {timeline.length > 0 && (
                <div className="mb-4">
                    <div className="text-[10px] text-white/30 tech-mono mb-2">SENTIMENT x MATCH EVENTS TIMELINE</div>
                    <ResponsiveContainer width="100%" height={180}>
                        <ComposedChart data={timeline}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                            <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 9 }} />
                            <YAxis yAxisId="sent" domain={[0, 10]} tick={{ fill: '#555', fontSize: 9 }} />
                            <YAxis yAxisId="runs" orientation="right" tick={{ fill: '#555', fontSize: 9 }} />
                            <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 10 }} />
                            <Bar yAxisId="runs" dataKey="runs" name="Runs" fill="#ffffff08" radius={[2, 2, 0, 0]}>
                                {timeline.map((d, i) => (
                                    <Cell key={i} fill={d.event_type === 'wicket' || d.event_type === 'multi_wicket' ? '#FF444440' : d.event_type === 'explosive' ? '#39FF1430' : '#ffffff08'} />
                                ))}
                            </Bar>
                            <Line yAxisId="sent" type="monotone" dataKey="team_a_sentiment" stroke={haColor} strokeWidth={2} dot={false} connectNulls name={ha?.short_name || 'Home'} />
                            <Line yAxisId="sent" type="monotone" dataKey="team_b_sentiment" stroke={aaColor} strokeWidth={2} dot={false} connectNulls name={aa?.short_name || 'Away'} />
                        </ComposedChart>
                    </ResponsiveContainer>
                    {/* Event markers */}
                    {sentEventsList.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                            {sentEventsList.map((e, i) => (
                                <span key={i} className={`text-[8px] px-1.5 py-0.5 rounded border tech-mono ${
                                    e.event_type === 'wicket' || e.event_type === 'multi_wicket' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                    e.event_type === 'explosive' || e.event_type === 'big' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                                    e.event_type === 'milestone' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                    'bg-white/5 text-white/30 border-white/10'
                                }`}>
                                    OV{e.over}: {e.reason?.slice(0, 25) || e.event_type}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-4 gap-2">
                {[
                    { label: 'Twitter', count: src.twitter || 0, icon: '𝕏' },
                    { label: 'Reddit', count: src.reddit || 0, icon: 'R' },
                    { label: 'News', count: src.news || 0, icon: 'N' },
                    { label: 'Trends', count: src.trends || 0, icon: 'G' },
                ].map((s, i) => (
                    <div key={i} className="text-center bg-white/5 rounded p-2">
                        <div className="text-lg font-bold text-white/20">{s.icon}</div>
                        <div className="text-xs text-white font-bold">{s.count}</div>
                        <div className="text-[9px] text-white/30">{s.label}</div>
                    </div>
                ))}
            </div>
            <div className="text-[9px] text-white/15 text-right mt-2 tech-mono">
                {sentiment?.current?.total_reports || 0} reports | Buzz: {sentiment?.current?.social_buzz || 0} items | Updated: {sentiment?.current?.last_updated?.slice(11, 19) || 'N/A'}
            </div>
        </div>
    );
}

/* ═══ WIN PROBABILITY TIMELINE CHART ═══ */
function WinProbChart({ data, battingTeam, bowlingTeam }) {
    if (!data || data.length === 0) {
        return (
            <div className="flex items-center justify-center h-[280px] text-white/20 text-sm">
                Waiting for ball-by-ball data...
            </div>
        );
    }
    const batColor = battingTeam?.color || '#00E5FF';
    const bowlColor = bowlingTeam?.color || '#FF2D95';
    return (
        <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={data}>
                <defs>
                    <linearGradient id="batGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={batColor} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={batColor} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 10 }} label={{ value: 'Over', position: 'bottom', fill: '#444', fontSize: 9 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#555', fontSize: 10 }} label={{ value: 'Win %', angle: -90, position: 'insideLeft', fill: '#444', fontSize: 9 }} />
                <Tooltip
                    contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 11 }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(v, name) => [`${v}%`, name === 'batting_wp' ? battingTeam?.short_name : bowlingTeam?.short_name]}
                />
                <ReferenceLine y={50} stroke="#333" strokeDasharray="5 5" />
                <Area type="monotone" dataKey="batting_wp" stroke={batColor} fill="url(#batGrad)" strokeWidth={2.5} name="batting_wp"
                    dot={false} animationDuration={1200} />
                <Line type="monotone" dataKey="bowling_wp" stroke={bowlColor} strokeWidth={1.5} strokeDasharray="4 3" name="bowling_wp"
                    dot={false} animationDuration={1200} />
            </ComposedChart>
        </ResponsiveContainer>
    );
}

/* ═══ LIVE BATTING SCORECARD ═══ */
function LiveScorecard({ batting, bowling }) {
    if ((!batting || batting.length === 0) && (!bowling || bowling.length === 0)) return null;
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {batting && batting.length > 0 && (
                <div className="glass-card p-5" data-testid="live-batting-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Crosshair className="w-4 h-4 text-cyan-400" />
                        <h3 className="heading-primary text-sm text-white">BATTING</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-white/30 tech-mono text-[9px]">
                                    <th className="text-left py-1 px-2">BATTER</th>
                                    <th className="text-right py-1 px-1">R</th>
                                    <th className="text-right py-1 px-1">B</th>
                                    <th className="text-right py-1 px-1">4s</th>
                                    <th className="text-right py-1 px-1">6s</th>
                                    <th className="text-right py-1 px-1">SR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batting.map((b, i) => (
                                    <tr key={i} className={`border-t border-white/5 ${i === 0 ? 'text-cyan-400' : 'text-white/60'}`}>
                                        <td className="py-1.5 px-2 font-medium">{b.name?.split(' ').pop()}</td>
                                        <td className="text-right py-1.5 px-1 font-bold">{b.runs}</td>
                                        <td className="text-right py-1.5 px-1">{b.balls}</td>
                                        <td className="text-right py-1.5 px-1">{b.fours}</td>
                                        <td className="text-right py-1.5 px-1">{b.sixes}</td>
                                        <td className="text-right py-1.5 px-1">{b.sr || (b.balls > 0 ? ((b.runs / b.balls) * 100).toFixed(1) : '0.0')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {bowling && bowling.length > 0 && (
                <div className="glass-card p-5" data-testid="live-bowling-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Flame className="w-4 h-4 text-orange-400" />
                        <h3 className="heading-primary text-sm text-white">BOWLING</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-white/30 tech-mono text-[9px]">
                                    <th className="text-left py-1 px-2">BOWLER</th>
                                    <th className="text-right py-1 px-1">O</th>
                                    <th className="text-right py-1 px-1">M</th>
                                    <th className="text-right py-1 px-1">R</th>
                                    <th className="text-right py-1 px-1">W</th>
                                    <th className="text-right py-1 px-1">ECON</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bowling.map((bw, i) => (
                                    <tr key={i} className={`border-t border-white/5 ${bw.wickets > 0 ? 'text-orange-400' : 'text-white/60'}`}>
                                        <td className="py-1.5 px-2 font-medium">{bw.name?.split(' ').pop()}</td>
                                        <td className="text-right py-1.5 px-1">{bw.overs}</td>
                                        <td className="text-right py-1.5 px-1">{bw.maidens}</td>
                                        <td className="text-right py-1.5 px-1">{bw.runs}</td>
                                        <td className="text-right py-1.5 px-1 font-bold">{bw.wickets}</td>
                                        <td className="text-right py-1.5 px-1">{bw.economy}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ═══ MAIN LIVE MATCH PAGE ═══ */
export default function LiveMatchPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const fetchLive = useCallback(() => {
        API.get('/live').then(res => {
            setData(res.data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchLive();
        const interval = setInterval(fetchLive, 15000);
        return () => clearInterval(interval);
    }, [fetchLive]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
                <div className="w-8 h-8 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!data?.live) {
        const next = data?.upcoming_soon?.[0];
        const countdown = next?.date ? (() => {
            const diff = new Date(next.date) - new Date();
            if (diff <= 0) return 'STARTING SOON';
            const h = Math.floor(diff / 3600000);
            const m = Math.floor((diff % 3600000) / 60000);
            return `${h}h ${m}m`;
        })() : null;

        return (
            <div className="space-y-6" data-testid="live-match-page">
                <div className="glass-card p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Activity className="w-5 h-5 text-red-400" />
                            <h2 className="heading-primary text-base md:text-xl text-white">LIVE MATCH COMMAND CENTER</h2>
                        </div>
                        <div className="text-[10px] text-white/30 tech-mono">AUTO-REFRESH 15s</div>
                    </div>
                </div>
                {next ? (
                    <PreMatchPanel match={next} countdown={countdown} navigate={navigate} />
                ) : (
                    <div className="glass-card p-12 text-center">
                        <Radio className="w-12 h-12 text-white/10 mx-auto mb-4" />
                        <div className="heading-primary text-xl text-white/40">NO UPCOMING MATCHES</div>
                        <div className="text-sm text-white/20 mt-2">Check back when IPL 2026 matches are scheduled</div>
                    </div>
                )}
                {data?.upcoming_soon?.length > 1 && (
                    <div className="glass-card p-5">
                        <div className="text-xs text-white/30 tech-mono mb-3">ALSO COMING UP</div>
                        <div className="space-y-2">
                            {data.upcoming_soon.slice(1).map((m, i) => (
                                <div key={i} className="p-3 bg-white/5 rounded-lg flex items-center justify-between cursor-pointer hover:bg-white/10 transition-colors"
                                    onClick={() => navigate('/ai-match-lab')} data-testid={`upcoming-match-${i + 1}`}>
                                    <div className="flex items-center gap-3">
                                        <Clock className="w-4 h-4 text-yellow-400" />
                                        <span className="text-sm text-white">{m.home_team?.short_name} vs {m.away_team?.short_name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-white/30">{m.venue?.city}</span>
                                        <ChevronRight className="w-3 h-3 text-white/20" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    /* ═══ LIVE MATCH VIEW ═══ */
    const match = data.matches[0];
    const batTeam = match.batting_team;
    const bowlTeam = match.bowling_team;
    const batColor = batTeam?.color || '#00E5FF';
    const bowlColor = bowlTeam?.color || '#FF2D95';
    const isFirstInnings = !match.is_second_innings;

    return (
        <div className="space-y-5" data-testid="live-match-page">
            {/* Header */}
            <div className="glass-card p-5">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <Activity className="w-5 h-5 text-red-400" />
                        <h2 className="heading-primary text-base md:text-lg text-white">LIVE MATCH INTELLIGENCE</h2>
                        <div className="px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded-full tech-mono border border-red-500/30 animate-pulse">LIVE</div>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="text-[10px] text-white/30 tech-mono">{match.innings_status}</span>
                        <span className="text-[10px] text-white/20 tech-mono">AUTO-REFRESH 15s</span>
                    </div>
                </div>
            </div>

            {/* Score Header */}
            <div className="glass-card p-6 border-l-4 border-l-red-500" data-testid="live-score-header">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-8">
                        {/* Bowling team / non-striker */}
                        <div className="text-center">
                            <div className="heading-primary text-lg" style={{ color: bowlColor }}>{bowlTeam?.short_name}</div>
                            {isFirstInnings ? (
                                <div className="text-sm text-white/30 italic mt-1">Yet to bat</div>
                            ) : (
                                <>
                                    <div className="text-2xl font-bold text-white">{match.match?.home_score || match.match?.away_score || '—'}</div>
                                    <div className="tech-mono text-[10px] text-white/30">{match.match?.overs_home || match.match?.overs_away || '—'} OV</div>
                                </>
                            )}
                        </div>
                        <div className="text-white/20 tech-mono text-xs">VS</div>
                        {/* Batting team */}
                        <div className="text-center">
                            <div className="heading-primary text-lg" style={{ color: batColor }}>{batTeam?.short_name}</div>
                            <div className="text-3xl font-bold" style={{ color: batColor }}>{match.current_score}/{match.wickets}</div>
                            <div className="tech-mono text-[10px] text-white/30">{match.overs_done} OV</div>
                        </div>
                    </div>
                    <div className="text-right">
                        {isFirstInnings ? (
                            <>
                                <div className="tech-mono text-[10px] text-white/30">PROJECTED</div>
                                <div className="heading-primary text-3xl text-yellow-400">{match.target}</div>
                                <div className="text-xs text-white/40">1st innings — {match.venue?.name}</div>
                            </>
                        ) : (
                            <>
                                <div className="tech-mono text-[10px] text-white/30">TARGET</div>
                                <div className="heading-primary text-3xl text-yellow-400">{match.target}</div>
                                <div className="text-xs text-white/40">Need {match.target - match.current_score} from {match.overs_remaining} overs</div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="glass-card p-4 text-center" data-testid="crr">
                    <div className="tech-mono text-[10px] text-white/30">CURRENT RUN RATE</div>
                    <div className="heading-primary text-3xl text-cyan-400">{match.current_run_rate}</div>
                </div>
                <div className="glass-card p-4 text-center" data-testid="rrr">
                    <div className="tech-mono text-[10px] text-white/30">REQUIRED RUN RATE</div>
                    <div className={`heading-primary text-3xl ${match.required_run_rate > match.current_run_rate ? 'text-red-400' : 'text-green-400'}`}>
                        {match.is_second_innings ? match.required_run_rate : '-'}
                    </div>
                </div>
                <div className="glass-card p-4 text-center" data-testid="momentum">
                    <div className="tech-mono text-[10px] text-white/30">MOMENTUM</div>
                    <div className={`heading-primary text-3xl ${match.momentum_index > 50 ? 'text-green-400' : 'text-red-400'}`}>{match.momentum_index}</div>
                </div>
                <div className="glass-card p-4 text-center" data-testid="pressure">
                    <div className="tech-mono text-[10px] text-white/30">PRESSURE</div>
                    <div className={`heading-primary text-3xl ${match.pressure_index > 60 ? 'text-red-400' : 'text-yellow-400'}`}>{match.pressure_index}</div>
                </div>
            </div>

            {/* Win Probability + Timeline */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-4 glass-card p-5" data-testid="win-probability">
                    <h3 className="heading-primary text-sm text-white mb-4">WIN PROBABILITY</h3>
                    <div className="space-y-4">
                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="tech-mono text-xs" style={{ color: batColor }}>{batTeam?.short_name}</span>
                                <span className="heading-primary text-2xl" style={{ color: batColor }}>{match.batting_team_win_pct}%</span>
                            </div>
                            <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${match.batting_team_win_pct}%`, backgroundColor: batColor }} />
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between mb-2">
                                <span className="tech-mono text-xs" style={{ color: bowlColor }}>{bowlTeam?.short_name}</span>
                                <span className="heading-primary text-2xl" style={{ color: bowlColor }}>{match.bowling_team_win_pct}%</span>
                            </div>
                            <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${match.bowling_team_win_pct}%`, backgroundColor: bowlColor }} />
                            </div>
                        </div>
                    </div>
                    <div className="mt-5 space-y-2 pt-4 border-t border-white/5">
                        <div className="flex justify-between">
                            <span className="text-xs text-white/40">Last 5 Overs</span>
                            <span className="text-sm text-white font-bold">{match.last_5_overs?.runs}/{match.last_5_overs?.wickets}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-xs text-white/40">Wickets Left</span>
                            <span className="text-sm text-white font-bold">{10 - match.wickets}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-xs text-white/40">Venue</span>
                            <span className="text-xs text-white/50">{match.venue?.name}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-xs text-white/40">Venue Avg (1st Inn)</span>
                            <span className="text-sm text-white font-bold">{match.venue?.avg_first_innings || '-'}</span>
                        </div>
                    </div>
                </div>

                {/* Win Probability Timeline */}
                <div className="lg:col-span-8 glass-card p-5" data-testid="win-prob-timeline">
                    <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        <h3 className="heading-primary text-sm text-white">WIN PROBABILITY TIMELINE</h3>
                        <div className="flex items-center gap-2 ml-auto">
                            <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded" style={{ backgroundColor: batColor }} /><span className="text-[9px] text-white/30">{batTeam?.short_name}</span></div>
                            <div className="flex items-center gap-1"><div className="w-3 h-0.5 rounded" style={{ backgroundColor: bowlColor, opacity: 0.6 }} /><span className="text-[9px] text-white/30">{bowlTeam?.short_name}</span></div>
                        </div>
                    </div>
                    <WinProbChart data={match.win_probability_timeline} battingTeam={batTeam} bowlingTeam={bowlTeam} />
                </div>
            </div>

            {/* Live Scorecard */}
            <LiveScorecard batting={match.live_batting} bowling={match.live_bowling} />

            {/* Run Rate Chart + Over by Over */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                <div className="lg:col-span-8 glass-card p-5" data-testid="run-rate-chart">
                    <div className="flex items-center gap-2 mb-3">
                        <BarChart2 className="w-4 h-4 text-cyan-400" />
                        <h3 className="heading-primary text-sm text-white">RUN RATE PROGRESSION</h3>
                    </div>
                    {match.over_by_over?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={220}>
                            <AreaChart data={match.over_by_over}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                                <XAxis dataKey="over" tick={{ fill: '#555', fontSize: 10 }} />
                                <YAxis tick={{ fill: '#555', fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #333', borderRadius: 8, fontSize: 11 }} />
                                {match.is_second_innings && match.required_run_rate > 0 && (
                                    <ReferenceLine y={match.required_run_rate} stroke="#FF2D95" strokeDasharray="3 3" />
                                )}
                                <Area type="monotone" dataKey="run_rate" stroke="#00E5FF" fill="#00E5FF" fillOpacity={0.1} strokeWidth={2} />
                                <Line type="monotone" dataKey="cumulative" stroke="#39FF14" strokeWidth={1.5} dot={false} />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-[220px] text-white/20 text-sm">Waiting for data...</div>
                    )}
                </div>

                {/* Over by Over Strip */}
                <div className="lg:col-span-4 glass-card p-5" data-testid="over-by-over">
                    <h3 className="heading-primary text-sm text-white mb-3">OVER BY OVER</h3>
                    <div className="grid grid-cols-4 gap-2 max-h-[220px] overflow-y-auto">
                        {(match.over_by_over || []).map((o, i) => (
                            <div key={i} className="text-center p-2 bg-white/5 rounded-lg">
                                <div className="tech-mono text-[9px] text-white/30">OV {o.over}</div>
                                <div className={`heading-primary text-lg ${o.runs >= 12 ? 'text-green-400' : o.runs >= 8 ? 'text-yellow-400' : 'text-white/60'}`}>{o.runs}</div>
                                <div className="text-[9px] text-white/20">{o.cumulative}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Sentiment Tracker */}
            <SentimentTracker matchData={match} />

            {/* Footer */}
            <div className="text-center text-[10px] text-white/10 tech-mono">
                LIVE MATCH INTELLIGENCE — V-ONE AI v3.0 — {match.venue?.name}, {match.venue?.city} — AUTO-REFRESH 15s
            </div>
        </div>
    );
}
